import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import MainLayout from './layouts/MainLayout';
import Login from './pages/auth/Login';
import Dashboard from './pages/dashboard/Dashboard';
import CandidatesList from './pages/candidates/CandidatesList';
import CandidateProfile from './pages/candidates/CandidateProfile';
import PotentialCandidatesList from './pages/potential-candidates/PotentialCandidatesList';
import PositionsList from './pages/positions/PositionsList';
import PositionDetails from './pages/positions/PositionDetails';
import CompaniesList from './pages/companies/CompaniesList';
import CompanyProfile from './pages/companies/CompanyProfile';
import InterviewsList from './pages/interviews/InterviewsList';
import InterviewDetails from './pages/interviews/InterviewDetails';
import DailyReporting from './pages/reports/DailyReporting';
import CandidateUpdates from './pages/reports/CandidateUpdates';
import EmailCenter from './pages/email/EmailCenter';
import CandidatePortal from './pages/candidate/CandidatePortal';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#1d4ed8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
      contrastText: '#ffffff',
    },
    background: {
      default: 'linear-gradient(135deg, #0a0e27 0%, #1a1b3a 25%, #2a2d47 50%, #1e2139 75%, #0f1419 100%)',
      paper: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8), rgba(51, 65, 85, 0.6))',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#1d4ed8',
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    h1: {
      fontWeight: 800,
      letterSpacing: '-0.02em',
      background: 'linear-gradient(135deg, #8AA6F9 0%, #F7B808 50%, #ffffff 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    h2: {
      fontWeight: 700,
      color: '#ffffff',
    },
    h3: {
      fontWeight: 700,
      color: '#ffffff',
    },
    h4: {
      fontWeight: 600,
      color: '#ffffff',
    },
    h5: {
      fontWeight: 600,
      color: '#ffffff',
    },
    h6: {
      fontWeight: 600,
      color: '#ffffff',
    },
    body1: {
      color: 'rgba(255, 255, 255, 0.87)',
    },
    body2: {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: 'linear-gradient(135deg, #0a0e27 0%, #1a1b3a 25%, #2a2d47 50%, #1e2139 75%, #0f1419 100%)',
          minHeight: '100vh',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8), rgba(51, 65, 85, 0.6))',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(59, 130, 246, 0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
          transition: 'all 0.3s ease',
        },
        contained: {
          background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #1d4ed8 100%)',
          boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%, #3b82f6 100%)',
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 16px rgba(37, 99, 235, 0.4)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        outlined: {
          border: '1px solid rgba(59, 130, 246, 0.3)',
          color: '#60a5fa',
          background: 'rgba(59, 130, 246, 0.05)',
          backdropFilter: 'blur(10px)',
          '&:hover': {
            border: '1px solid rgba(59, 130, 246, 0.5)',
            background: 'rgba(59, 130, 246, 0.1)',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8), rgba(51, 65, 85, 0.6))',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(59, 130, 246, 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4), 0 6px 12px rgba(59, 130, 246, 0.2)',
            borderColor: 'rgba(59, 130, 246, 0.4)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #0B101C 0%, #0847F7 50%, #4A21C2 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: '2px solid rgba(138, 166, 249, 0.5)',
          boxShadow: '0 8px 32px rgba(8, 71, 247, 0.3), 0 2px 8px rgba(138, 166, 249, 0.2)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(145deg, rgba(11, 16, 28, 0.95), rgba(30, 41, 59, 0.9))',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            background: 'rgba(30, 41, 59, 0.6)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: 8,
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '& fieldset': {
              border: 'none',
            },
            '&:hover': {
              borderColor: 'rgba(59, 130, 246, 0.5)',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.1)',
            },
            '&.Mui-focused': {
              borderColor: 'rgba(59, 130, 246, 0.8)',
              boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: '#60a5fa',
            },
          },
          '& .MuiOutlinedInput-input': {
            color: 'white',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backdropFilter: 'blur(10px)',
        },
        standardError: {
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          color: '#fecaca',
        },
        standardWarning: {
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          color: '#fcd34d',
        },
        standardSuccess: {
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          color: '#86efac',
        },
        standardInfo: {
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          color: '#93c5fd',
        },
      },
    },
  },
});

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0e27 0%, #1a1b3a 25%, #2a2d47 50%, #1e2139 75%, #0f1419 100%)',
        color: '#ffffff'
      }}>
        <div style={{
          background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8), rgba(51, 65, 85, 0.6))',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(59, 130, 246, 0.1)',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div className="professional-spinner" style={{ margin: '0 auto 20px auto' }} />
          <div style={{
            color: '#4a9eff',
            fontWeight: 600,
            fontSize: '1.1em',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
          }}>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" />;
};

const AppRoutes: React.FC = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />

      {/* Admin Routes */}
      <Route path="/dashboard" element={<PrivateRoute><MainLayout><Dashboard /></MainLayout></PrivateRoute>} />
      <Route path="/candidates" element={<PrivateRoute><MainLayout><CandidatesList /></MainLayout></PrivateRoute>} />
      <Route path="/candidates/:id" element={<PrivateRoute><MainLayout><CandidateProfile /></MainLayout></PrivateRoute>} />
      <Route path="/potential-candidates" element={<PrivateRoute><MainLayout><PotentialCandidatesList /></MainLayout></PrivateRoute>} />
      <Route path="/positions" element={<PrivateRoute><MainLayout><PositionsList /></MainLayout></PrivateRoute>} />
      <Route path="/positions/:id" element={<PrivateRoute><MainLayout><PositionDetails /></MainLayout></PrivateRoute>} />
      <Route path="/companies" element={<PrivateRoute><MainLayout><CompaniesList /></MainLayout></PrivateRoute>} />
      <Route path="/companies/:id" element={<PrivateRoute><MainLayout><CompanyProfile /></MainLayout></PrivateRoute>} />
      <Route path="/interviews" element={<PrivateRoute><MainLayout><InterviewsList /></MainLayout></PrivateRoute>} />
      <Route path="/interviews/:id" element={<PrivateRoute><MainLayout><InterviewDetails /></MainLayout></PrivateRoute>} />
      <Route path="/reports/daily" element={<PrivateRoute><MainLayout><DailyReporting /></MainLayout></PrivateRoute>} />
      <Route path="/reports/candidate-updates" element={<PrivateRoute><MainLayout><CandidateUpdates /></MainLayout></PrivateRoute>} />
      <Route path="/email-center" element={<PrivateRoute><MainLayout><EmailCenter /></MainLayout></PrivateRoute>} />

      {/* Public Candidate Portal Routes */}
      <Route path="/:nameSlug" element={<CandidatePortal />} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
